import { configureStore, createListenerMiddleware, Middleware } from "@reduxjs/toolkit";
import loadingReducer from "./loadingSlice";
import dialogReducer from "./dialogSlice";
import authReducer, { login, logout } from "./authSlice";
import notificationReducer from "./notificationSlice";
import cryptoFilterReducer from "./cryptoFilterSlice";
import watchlistReducer from "./watchlistSlice";
import cashoutReducer, { resetCashoutState } from "./cashout/cashoutSlice";
import currencyReducer from "./currencySlice";
import { registerDevice, unlinkDevice } from "../services/pushNotifications";
import { resetNotificationState } from "../services/notificationState";

const listenerMiddleware = createListenerMiddleware();
let lastRegisterAt = 0; // throttle for pushRegisterMiddleware; reset on logout

// Listen for logout action and reset cashout state
listenerMiddleware.startListening({
  actionCreator: logout,
  effect: (action, listenerApi) => {
    // Dispatch reset action for cashout redux slice
    listenerApi.dispatch(resetCashoutState());
  },
});

// Runs BEFORE the logout reducer clears the auth token so the web push device
// can be unlinked (best-effort, never blocks or throws).
const pushUnlinkMiddleware: Middleware = () => (next) => (action: any) => {
  if (action?.type === logout.type) {
    try {
      void unlinkDevice();
      resetNotificationState();
      lastRegisterAt = 0;
    } catch {
      /* best-effort */
    }
  }
  return next(action);
};

// Registers this browser for web push as soon as a VERIFIED user is signed in (login, or the last signup step), so the
// device is known right away instead of waiting for the next page load. Runs AFTER the login reducer has saved the
// session. registerDevice() itself does nothing unless web push is enabled, supported and already permitted, and it
// never prompts. Throttled because several auth screens dispatch login() in a row.
const REGISTER_THROTTLE_MS = 30_000;
const pushRegisterMiddleware: Middleware = () => (next) => (action: any) => {
  const result = next(action);
  if (action?.type === login.type && action?.payload?.isVerify && Date.now() - lastRegisterAt > REGISTER_THROTTLE_MS) {
    lastRegisterAt = Date.now();
    void registerDevice();
  }
  return result;
};

const store = configureStore({
  reducer: {
    loading: loadingReducer,
    dialog: dialogReducer,
    auth: authReducer,
    notifications: notificationReducer,
    cryptoFilter: cryptoFilterReducer,
    watchlist: watchlistReducer,
    cashout: cashoutReducer,
    currency: currencyReducer,
  },
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware().prepend(pushUnlinkMiddleware, pushRegisterMiddleware, listenerMiddleware.middleware),
});

// Export types for TypeScript
export type RootState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;

export default store;